import { Component, forwardRef, Inject, NgModuleRef, OnInit } from '@angular/core';
import {
  ControlValueAccessor,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { debounceTime, map, tap } from 'rxjs/operators';
import { DestroySubscribers } from '../../../decorators/destroy-subscribers.decorator';
import { InfoModal } from '../../../modals/info/info.modal';
import { IMODALS_SERVICE, IModalsService } from '@platform500services/p500-ui-kit';
import { MODAL_TOOLTIP, MODAL_TOOLTIP_KEY_ENUM } from '../controls-with-hint/hints';

@Component({
  templateUrl: './offer-url-params.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => OfferUrlParamsComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => OfferUrlParamsComponent),
      multi: true,
    },
  ],
})
@DestroySubscribers()
export class OfferUrlParamsComponent implements ControlValueAccessor, OnInit, Validator {
  public form = new UrlFormArray([]);
  public subscribers: Record<string, Subscription> = {};
  public onChange: (value: any) => void = () => {};
  public onTouch = () => {};
  public modalTooltipEnum = MODAL_TOOLTIP_KEY_ENUM;

  constructor(@Inject(IMODALS_SERVICE) public modalsService: IModalsService, private moduleRef: NgModuleRef<any>) {}

  public ngOnInit(): void {
    this.subscribers.formChanges = this.form.valueChanges.pipe(debounceTime(200)).subscribe((values: IMapUrlParams[]) => {
      const mapValue: IUrlParams[] = values.map((value) => {
        const visibilityMap = new Set<string>(value.public_keys || []);
        return {
          title: value.title,
          default_value: value.default_value,
          available_values: value.available_values.map((item) => ((item.is_public = visibilityMap.has(item.value)), item)),
          type: value.type,
        };
      });
      this.onChange(JSON.stringify(mapValue));
      this.onTouch();
    });
  }

  public addForm(type: IUrlParams['type'] = 'free'): void {
    const availableValuesCtr = new AvailableValuesFormArray([]);
    const defaultValueCtr = new UntypedFormControl(null, Validators.required);
    const publicKeysCtr = new UntypedFormControl([]);
    const group = new UrlFormGroup({
      title: new UntypedFormControl(null, [Validators.required, Validators.maxLength(40)]),
      available_values: availableValuesCtr,
      default_value: defaultValueCtr,
      public_keys: publicKeysCtr,
      type: new UntypedFormControl(type),
    });
    group.availableValues = availableValuesCtr.valueChanges.pipe(
      debounceTime(200),
      map((values: IMapUrlParams['available_values']) =>
        values.reduce((acc, item) => {
          if (item.value && item.label) {
            acc[item.value] = item.value;
          }
          return acc;
        }, {})
      ),
      tap((values) => {
        if (!values[defaultValueCtr.value]) {
          defaultValueCtr.reset();
          publicKeysCtr.reset();
        }
      })
    );
    this.form.push(group);
  }

  public addAvailableForm(i: number): void {
    const valueCtr = new UntypedFormControl(null);
    const idx = this.form.controls[i].controls.available_values.length;
    valueCtr.setValidators([
      Validators.required,
      Validators.maxLength(20),
      () => {
        const values: IMapUrlParams['available_values'] = this.form.controls[i].controls.available_values.value;
        return values.some((item, j) => j !== idx && item.value === valueCtr.value) ? { customValidate: 'key already exist' } : null;
      },
    ]);
    const group = new AvailableValuesFormGroup({
      value: valueCtr,
      label: new UntypedFormControl(null, [Validators.required, Validators.maxLength(40)]),
    });
    this.form.controls[i].controls.available_values.push(group);
  }

  public removeForm(i: number): void {
    this.form.removeAt(i);
  }

  public get disabledFree(): boolean {
    return this.form.controls.some((ctr) => ctr.value.type === 'advertiser');
  }

  public removeAvailableForm(i: number, j: number): void {
    this.form.controls[i].controls.available_values.removeAt(j);
  }

  public validate(): ValidationErrors | null {
    return this.form.invalid ? { errors: true } : null;
  }

  public writeValue(obj: any): void {
    try {
      const params: IMapUrlParams[] = JSON.parse(obj);
      params.forEach((item, i) => {
        this.addForm();
        item.available_values.forEach(() => this.addAvailableForm(i));
        item.public_keys = item.available_values.filter((value) => value.is_public).map((value) => value.value);
      });
      this.form.patchValue(params);
      this.form.controls.forEach((ctr) => {
        setTimeout(() => {
          ctr.controls.available_values.updateValueAndValidity();
        }, 100);
      });
    } catch {}
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  public openTooltipModal(type: string): void {
    this.modalsService.open(
      InfoModal,
      {
        data: {
          title:
            type === 'advertiser'
              ? $localize`:@@advertiser.parameters:Advertiser Parameters`
              : $localize`:@@common.free_parameters:Free Parameters`,
          subTitle: $localize`:@@common.info:Info`,
          body: MODAL_TOOLTIP[
            type === 'advertiser'
              ? MODAL_TOOLTIP_KEY_ENUM.OFFER_URL_ADVERTISER_PARAMETERS
              : MODAL_TOOLTIP_KEY_ENUM.OFFER_URL_FREE_PARAMETERS
          ],
          footerType: null,
          hasCopy: false,
          class: '-info',
        },
      },
      this.moduleRef
    );
  }

  protected readonly MODAL_TOOLTIP_KEY_ENUM = MODAL_TOOLTIP_KEY_ENUM;
}

interface IAvialibleValue {
  is_public?: boolean;
  label: string;
  value: string;
}

export interface IUrlParams {
  title: string;
  available_values: IAvialibleValue[];
  default_value: string;
  type: 'free' | 'advertiser';
}

interface IMapUrlParams extends IUrlParams {
  public_keys?: string[];
}

class UrlFormGroup extends UntypedFormGroup {
  controls: {
    title: UntypedFormControl;
    available_values: AvailableValuesFormArray;
    default_value: UntypedFormControl;
    public_keys: UntypedFormControl;
  };
  public availableValues: Observable<Record<string, string>>;
}

class AvailableValuesFormGroup extends UntypedFormGroup {
  controls: {
    key: UntypedFormControl;
    value: UntypedFormControl;
  };
}

class UrlFormArray extends UntypedFormArray {
  controls: UrlFormGroup[];
}

class AvailableValuesFormArray extends UntypedFormArray {
  controls: AvailableValuesFormGroup[];
}
